import {useIntl} from 'react-intl';
import {useQuery} from 'react-query';
import {useNavigate} from 'react-router-dom';
import TicketList, {OpenTicketFilter} from './TicketList';
import TabComponent from '../../../app/common/components/TabComponent';
import {useState} from 'react';
import {getArchivedTicket, getOpenTickets} from '../Api/tickets';
import {TicketFromApi} from '../../../app/interfaces/ticket';

const isNouveau = (ticket: TicketFromApi): boolean =>
  ticket.state !== null && ticket.state.toString().toLowerCase() === 'nouveau';

interface TicketStatCardProps {
  color: string;
  count: number;
  label: string;
  icon: string;
  onClick?: () => void;
}

const TicketStatCard = ({
  color,
  count,
  label,
  icon,
  onClick,
}: TicketStatCardProps): JSX.Element => (
  <div
    role={onClick ? 'button' : undefined}
    onClick={onClick}
    className="d-flex align-items-center bg-white rounded px-4 py-3 flex-grow-1"
    style={{
      minWidth: 0,
      cursor: onClick ? 'pointer' : undefined,
      border: '1px solid #e6e6e6',
    }}
  >
    <span
      className="rounded-circle me-3"
      style={{width: 8, height: 8, backgroundColor: color, flexShrink: 0}}
    />
    <div className="d-flex flex-column">
      <span className="fw-bold fs-3 lh-1">{count}</span>
      <span className="text-muted small mt-1">{label}</span>
    </div>
    <i className={`bi ${icon} ms-auto ps-3 fs-2`} style={{color, flexShrink: 0}} />
  </div>
);

const TicketDisplay = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ticket-open');
  const [openFilter, setOpenFilter] = useState<OpenTicketFilter>('all');

  const {data: openTickets} = useQuery<TicketFromApi[]>(['tickets', 'open'], () =>
    getOpenTickets()
  );
  const {data: archivedTickets} = useQuery<TicketFromApi[]>(['tickets', 'archived'], () =>
    getArchivedTicket()
  );

  const nouveauCount = (openTickets ?? []).filter(isNouveau).length;
  const enCoursCount = (openTickets ?? []).length - nouveauCount;
  const archiveCount = (archivedTickets ?? []).length;

  const tabData = [
    {
      title: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.OPEN'}),
      data: {archived: false},
      key: 'ticket-open',
    },
    {
      title: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.ARCHIVED'}),
      data: {archived: true},
      key: 'ticket-archived',
    },
  ];

  return (
    <>
      <div className="d-flex gap-3 mb-4 flex-wrap">
        <TicketStatCard
          color="#3699FF"
          count={nouveauCount}
          label={intl.formatMessage({id: 'DASHBOARD.TICKETS.STATUS.NEW'})}
          icon="bi-envelope"
          onClick={() => {
            setActiveTab('ticket-open');
            setOpenFilter('nouveau');
          }}
        />
        <TicketStatCard
          color="#F1A340"
          count={enCoursCount}
          label={intl.formatMessage({id: 'DASHBOARD.TICKETS.STATUS.IN_PROGRESS'})}
          icon="bi-hourglass-split"
          onClick={() => {
            setActiveTab('ticket-open');
            setOpenFilter('pris-en-charge');
          }}
        />
        <TicketStatCard
          color="#1BC5BD"
          count={archiveCount}
          label={intl.formatMessage({id: 'TICKETS.STAT.ARCHIVED'})}
          icon="bi-archive"
          onClick={() => setActiveTab('ticket-archived')}
        />
      </div>

      <TabComponent
        tabData={tabData.map((tab) => ({
          title: tab.title,
          key: tab.key,
          content: (
            <TicketList
              archived={tab.data.archived}
              openFilter={tab.data.archived ? undefined : openFilter}
              onOpenFilterChange={tab.data.archived ? undefined : setOpenFilter}
              headerRightContent={
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center"
                  onClick={() => navigate('/support')}
                >
                  <i className="bi bi-plus-square me-2" />
                  {intl.formatMessage({id: 'SUPPORT.NEW_REQUEST'})}
                </button>
              }
            />
          ),
        }))}
        defaultTabKey={'ticket-open'}
        activeKey={activeTab}
        onTabChange={(key) => setActiveTab(key)}
      />
    </>
  );
};

export default TicketDisplay;
